import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Trash2, FileText, KeyRound } from "lucide-react";
import { PageHeader } from "../components/shared/PageHeader";
import { DataTable } from "../components/shared/DataTable";
import { StatusBadge } from "../components/shared/StatusBadge";
import { ConfirmDialog } from "../components/shared/ConfirmDialog";
import { ErrorState } from "../components/shared/ErrorState";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Skeleton } from "../components/ui/skeleton";
import { CustomerFormDialog } from "../components/customers/CustomerFormDialog";
import { CustomerActivityLog } from "../components/customers/CustomerActivityLog";
import { DeliveryInvestigation } from "../components/customers/DeliveryInvestigation";
import { useAuth } from "../context/AuthContext";
import { apiClient, getApiErrorMessage } from "../lib/apiClient";
import { formatCurrency, formatDate } from "../lib/utils";
import { useTaxConfig, breakdownFromPayment } from "../lib/tax";
import { ReceiptDialog } from "../components/payments/ReceiptDialog";
import { receiptFromPayment } from "../components/payments/PaymentReceipt";
import { subscriptionStatusMeta, deliveryStatusMeta, paymentStatusMeta, customerStatusMeta } from "../lib/statusMaps";

function Detail({ label, value, testId }) {
  return (
    <div data-testid={testId}>
      <p className="text-xs uppercase tracking-wide text-text-muted">{label}</p>
      <p className="mt-1 text-sm font-medium text-text-main">{value || "—"}</p>
    </div>
  );
}

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPerm } = useAuth();
  const canViewActivity = hasPerm("customer_activity_log.view");
  const queryClient = useQueryClient();
  const [state, setState] = useState({ loading: true, error: null, customer: null });
  const [history, setHistory] = useState({ loading: true, subscriptions: [], deliveries: [], payments: [] });
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const taxCfg = useTaxConfig();

  const loadCustomer = useCallback(async () => {
    setState({ loading: true, error: null, customer: null });
    try {
      const { data } = await apiClient.get(`/customers/${id}`);
      setState({ loading: false, error: null, customer: data });
    } catch (error) {
      setState({ loading: false, error: getApiErrorMessage(error, "Could not load this customer."), customer: null });
    }
  }, [id]);

  const loadHistory = useCallback(async () => {
    setHistory((h) => ({ ...h, loading: true }));
    const [subsRes, delivRes, payRes] = await Promise.allSettled([
      apiClient.get("/subscriptions", { params: { customer_id: id, limit: 100 } }),
      apiClient.get(`/deliveries/customer/${id}`, { params: { limit: 100 } }),
      apiClient.get(`/payments/customer/${id}`),
    ]);
    if (subsRes.status === "rejected") toast.error(getApiErrorMessage(subsRes.reason, "Could not load subscription history."));
    if (delivRes.status === "rejected") toast.error(getApiErrorMessage(delivRes.reason, "Could not load delivery history."));
    if (payRes.status === "rejected") toast.error(getApiErrorMessage(payRes.reason, "Could not load payment history."));
    setHistory({
      loading: false,
      subscriptions: subsRes.status === "fulfilled" ? subsRes.value.data.items || [] : [],
      deliveries: delivRes.status === "fulfilled" ? delivRes.value.data.items || [] : [],
      payments: payRes.status === "fulfilled" ? payRes.value.data || [] : [],
    });
  }, [id]);

  useEffect(() => {
    loadCustomer();
    loadHistory();
  }, [loadCustomer, loadHistory]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await apiClient.delete(`/customers/${id}`);
      toast.success("Customer deleted");
      queryClient.invalidateQueries({ queryKey: ["customers-list"] });
      navigate("/customers");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not delete this customer."));
    } finally {
      setDeleting(false);
      setDeleteOpen(false);
    }
  };

  const handleResetPassword = async () => {
    setResetting(true);
    try {
      await apiClient.post(`/customers/${id}/reset-password`);
      toast.success("Password reset. The customer's temporary password is the last 4 digits of their phone, and they'll be asked to set a new one on next login.");
      loadCustomer();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not reset this customer's password."));
    } finally {
      setResetting(false);
      setResetOpen(false);
    }
  };

  if (state.loading) {
    return (
      <div data-testid="customer-detail-page">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="mt-4 h-64 w-full" />
      </div>
    );
  }

  if (state.error || !state.customer) {
    return (
      <div data-testid="customer-detail-page">
        <PageHeader
          title="Customer"
          actions={
            <Button variant="secondary" onClick={() => navigate("/customers")} data-testid="customer-detail-back-button">
              <ArrowLeft size={16} strokeWidth={1.5} /> Back
            </Button>
          }
        />
        <ErrorState message={state.error || "Customer not found."} onRetry={loadCustomer} />
      </div>
    );
  }

  const c = state.customer;
  const meta = customerStatusMeta(c);
  const isDeleted = c.is_deleted === 1;
  const custName = `${c.firstname || ""} ${c.lastname || ""}`.trim() || `Customer #${c.id}`;
  // Latest APPROVED payment for a subscription (its receipt), if any.
  const paidForSub = (subId) =>
    (history.payments || [])
      .filter((p) => p.subscription_id === subId && p.status === 2)
      .sort((a, b) => (b.id || 0) - (a.id || 0))[0] || null;
  const openReceipt = (pay) => setReceiptData(receiptFromPayment(pay, breakdownFromPayment(pay, taxCfg), custName));
  const ReceiptButton = ({ pay, testId }) => (
    <Button
      variant="ghost" size="sm" className="ml-auto"
      disabled={!pay}
      title={pay ? "" : "Available after payment"}
      onClick={() => pay && openReceipt(pay)}
      data-testid={testId}
    ><FileText size={14} strokeWidth={1.5} /> Receipt</Button>
  );

  return (
    <div data-testid="customer-detail-page">
      <PageHeader
        title={`${c.firstname || ""} ${c.lastname || ""}`.trim() || `Customer #${c.id}`}
        description={`Customer ID #${c.id}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => navigate("/customers")} data-testid="customer-detail-back-button">
              <ArrowLeft size={16} strokeWidth={1.5} /> Back
            </Button>
            <Button variant="outline" onClick={() => setEditOpen(true)} disabled={isDeleted} data-testid="customer-detail-edit-button">
              <Pencil size={16} strokeWidth={1.5} /> Edit
            </Button>
            <Button variant="outline" onClick={() => setResetOpen(true)} disabled={isDeleted} data-testid="customer-detail-reset-password-button">
              <KeyRound size={16} strokeWidth={1.5} /> Reset Password
            </Button>
            <Button variant="danger" onClick={() => setDeleteOpen(true)} disabled={isDeleted} data-testid="customer-detail-delete-button">
              <Trash2 size={16} strokeWidth={1.5} /> Delete
            </Button>
          </div>
        }
      />

      <Card className="mb-6" data-testid="customer-overview-card">
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle>Customer Details</CardTitle>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {(() => {
              const pm = {
                temporary: { label: "Temporary — must change", variant: "warning" },
                phone_default: { label: "Default (phone) — never changed", variant: "info" },
                custom: { label: "Custom password set", variant: "success" },
              }[c.password_status] || null;
              return (
                <>
                  <StatusBadge
                    label={c.has_logged_in ? "Has logged in" : "Never logged in"}
                    variant={c.has_logged_in ? "success" : "deactivated"}
                  />
                  {pm && <StatusBadge label={pm.label} variant={pm.variant} />}
                  <StatusBadge label={meta.label} variant={meta.variant} />
                </>
              );
            })()}
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Detail label="Email" value={c.email} testId="customer-detail-email" />
          <Detail label="Phone" value={c.phone} testId="customer-detail-phone" />
          <Detail label="House / Unit No." value={c.house_no} testId="customer-detail-house-no" />
          <Detail label="Address" value={c.address} testId="customer-detail-address" />
          <Detail label="City" value={c.city} testId="customer-detail-city" />
          <Detail label="State" value={c.state} testId="customer-detail-state" />
          <Detail label="Postal Code" value={c.postal_code} testId="customer-detail-postal-code" />
        </CardContent>
      </Card>

      <Tabs defaultValue="subscriptions" data-testid="customer-history-tabs">
        <TabsList>
          <TabsTrigger value="subscriptions" data-testid="customer-tab-subscriptions">Subscriptions</TabsTrigger>
          <TabsTrigger value="deliveries" data-testid="customer-tab-deliveries">Deliveries</TabsTrigger>
          <TabsTrigger value="payments" data-testid="customer-tab-payments">Payments</TabsTrigger>
          {canViewActivity && (
            <>
              <TabsTrigger value="activity" data-testid="customer-tab-activity">Activity Log</TabsTrigger>
              <TabsTrigger value="investigation" data-testid="customer-tab-investigation">Investigation</TabsTrigger>
            </>
          )}
        </TabsList>

        <TabsContent value="subscriptions">
          <DataTable
            testId="customer-subscriptions-table"
            loading={history.loading}
            rows={history.subscriptions}
            emptyTitle="No subscriptions yet"
            emptyDescription="This customer has not been enrolled in any plan."
            columns={[
              { key: "id", header: "ID", render: (r) => (
                <button
                  type="button"
                  onClick={() => navigate(`/subscriptions/${r.id}`)}
                  className="font-medium text-primary underline-offset-2 hover:underline"
                  data-testid={`customer-subscription-${r.id}-link`}
                >
                  #{r.id}
                </button>
              ) },
              { key: "plan_name", header: "Plan", render: (r) => r.plan_name || `Plan #${r.plan_id}` },
              {
                key: "status", header: "Status",
                render: (r) => {
                  const m = subscriptionStatusMeta(r.status);
                  return <StatusBadge label={m.label} variant={m.variant} />;
                },
              },
              { key: "start_date", header: "Start", render: (r) => formatDate(r.start_date) },
              { key: "end_date", header: "End", render: (r) => formatDate(r.end_date) },
              { key: "price", header: "Price", render: (r) => formatCurrency(r.price) },
              { key: "bonus_tiffins", header: "Bonus", render: (r) => r.bonus_tiffins > 0 ? `+${r.bonus_tiffins}` : "—" },
              { key: "receipt", header: "", render: (r) => <ReceiptButton pay={paidForSub(r.id)} testId={`customer-subscription-${r.id}-receipt`} /> },
            ]}
          />
        </TabsContent>

        <TabsContent value="deliveries">
          <DataTable
            testId="customer-deliveries-table"
            loading={history.loading}
            rows={history.deliveries}
            emptyTitle="No deliveries yet"
            emptyDescription="No deliveries have been generated for this customer."
            columns={[
              { key: "id", header: "ID" },
              { key: "date", header: "Date", render: (r) => formatDate(r.date) },
              {
                key: "status", header: "Status",
                render: (r) => {
                  const m = deliveryStatusMeta(r.delivery_status);
                  return <StatusBadge label={m.label} variant={m.variant} />;
                },
              },
              { key: "eta", header: "ETA", render: (r) => r.eta || "—" },
              { key: "arrival", header: "Arrival", render: (r) => r.arrival || "—" },
            ]}
          />
        </TabsContent>

        <TabsContent value="payments">
          <DataTable
            testId="customer-payments-table"
            loading={history.loading}
            rows={history.payments}
            emptyTitle="No payments yet"
            emptyDescription="No payments have been recorded for this customer."
            columns={[
              { key: "id", header: "ID" },
              { key: "date", header: "Date", render: (r) => formatDate(r.date) },
              { key: "amout", header: "Amount", render: (r) => formatCurrency(r.amout) },
              { key: "payment_method_label", header: "Method", render: (r) => r.payment_method_label || "—" },
              {
                key: "status", header: "Status",
                render: (r) => {
                  const m = paymentStatusMeta(r.status, r.payment_method, r.collected_at);
                  return <StatusBadge label={m.label} variant={m.variant} />;
                },
              },
              { key: "receipt", header: "", render: (r) => <ReceiptButton pay={r.status === 2 ? r : null} testId={`customer-payment-${r.id}-receipt`} /> },
            ]}
          />
        </TabsContent>

        {canViewActivity && (
          <>
            <TabsContent value="activity">
              <CustomerActivityLog customerId={id} />
            </TabsContent>
            <TabsContent value="investigation">
              <DeliveryInvestigation customerId={id} />
            </TabsContent>
          </>
        )}
      </Tabs>

      <ReceiptDialog open={!!receiptData} onOpenChange={(v) => !v && setReceiptData(null)} data={receiptData} />

      <CustomerFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        customer={c}
        onSaved={() => {
          setEditOpen(false);
          loadCustomer();
          queryClient.invalidateQueries({ queryKey: ["customers-list"] });
        }}
      />
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete this customer?"
        description={`This soft-deletes ${c.firstname || "this customer"} and deactivates their login. This can't be undone from here.`}
        confirmLabel="Delete Customer"
        variant="danger"
        loading={deleting}
        onConfirm={handleDelete}
        testId="customer-delete-confirm-dialog"
      />
      <ConfirmDialog
        open={resetOpen}
        onOpenChange={setResetOpen}
        title="Reset this customer's password?"
        description={`${c.firstname || "This customer"}'s password will be set to a temporary password (the last 4 digits of their phone). They'll be required to create a new password the next time they sign in, and their current password will stop working.`}
        confirmLabel="Reset Password"
        variant="default"
        loading={resetting}
        onConfirm={handleResetPassword}
        testId="customer-reset-password-confirm-dialog"
      />
    </div>
  );
}
